import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { GraduationCap, Loader2, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { DateField } from "@/components/training/DateField";
import { Label } from "@/components/ui/label";
import { createTrainingNote, updateTrainingNote } from "@/lib/dentiumApi";
import { presets, errText } from "@/components/training/trainingUtils";

const EXAMPLES = [
  "Mañana estamos cerrados por una reparación técnica.",
  "Esta semana hay un 20% de descuento en blanqueamiento dental.",
  "La doctora García está de vacaciones hasta el lunes.",
];
const EMPTY = { text: "", starts_on: "", ends_on: "" };

export const TeachCard = ({ editing, onDone, cardRef }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  useEffect(() => {
    setForm(editing ? { text: editing.text, starts_on: editing.starts_on || "", ends_on: editing.ends_on || "" } : EMPTY);
  }, [editing]);
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const payload = { text: form.text, starts_on: form.starts_on || null, ends_on: form.ends_on || null, active: editing ? editing.active : true };
      return editing ? updateTrainingNote(editing.id, payload) : createTrainingNote(payload);
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ["training-notes"] });
      setForm(EMPTY);
      onDone?.();
      const msg = n.status === "activo" ? "El asistente ya lo tiene en cuenta desde el siguiente mensaje." : n.status === "programado" ? "Se activará automáticamente en la fecha indicada." : "Guardado.";
      toast.success(editing ? "Aviso actualizado" : "¡Aprendido!", { description: msg });
    },
    onError: (e) => toast.error(errText(e, "No se pudo guardar")),
  });

  const activePreset = presets().find((p) => p.starts_on === form.starts_on && p.ends_on === form.ends_on)?.key;
  return (
    <div ref={cardRef} className="rounded-2xl border border-[#E7E2DA] bg-white p-5 sm:p-6 scroll-mt-4" data-testid="teach-card">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-xl bg-[#1A1A1A] flex items-center justify-center"><GraduationCap className="h-4.5 w-4.5 text-white" /></span>
          <h2 className="text-lg font-medium text-[#1A1A1A]">{editing ? "Editar aviso" : "Enséñale algo"}</h2>
        </div>
        {editing && (
          <button data-testid="teach-cancel-edit" onClick={onDone} className="text-xs text-[#8A7E70] hover:text-[#1A1A1A] inline-flex items-center gap-1"><X className="h-3.5 w-3.5" /> Cancelar</button>
        )}
      </div>
      <p className="text-sm text-[#8A7E70] mb-4 ml-11">Escríbelo con tus palabras, como se lo dirías a una persona del equipo.</p>
      <Textarea data-testid="teach-text-input" value={form.text} onChange={(e) => set("text")(e.target.value)} rows={4} maxLength={3000}
        placeholder="Ej.: Mañana estamos cerrados por una reparación técnica." className="resize-none text-[15px]" />
      {!editing && !form.text && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="teach-examples">
          {EXAMPLES.map((ex) => (
            <button key={ex} type="button" onClick={() => set("text")(ex)} className="text-[11px] rounded-full border border-[#E7E2DA] bg-[#FAF8F5] hover:bg-[#F1EBE1] px-2.5 py-1 text-[#6B6157] transition-colors">{ex}</button>
          ))}
        </div>
      )}
      <div className="mt-4">
        <Label className="mb-1.5 block">¿Cuándo debe tenerlo en cuenta?</Label>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {presets().map((p) => (
            <button key={p.key} type="button" data-testid={`teach-preset-${p.key}`} onClick={() => setForm((s) => ({ ...s, starts_on: p.starts_on, ends_on: p.ends_on }))}
              className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${activePreset === p.key ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#4A4239] border-[#E7E2DA] hover:bg-[#F1EBE1]"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-[#8A7E70] mb-1 block">Desde</Label>
            <DateField testid="teach-starts-input" value={form.starts_on} onChange={set("starts_on")} />
          </div>
          <div>
            <Label className="text-xs text-[#8A7E70] mb-1 block">Hasta (incluido)</Label>
            <DateField testid="teach-ends-input" value={form.ends_on} min={form.starts_on} onChange={set("ends_on")} />
          </div>
        </div>
      </div>
      <button data-testid="teach-save-btn" disabled={!form.text.trim() || save.isPending} onClick={() => save.mutate()}
        className="mt-5 w-full h-11 rounded-md bg-[#1A1A1A] hover:bg-black text-white text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Guardar cambios" : "Enseñar al asistente"}
      </button>
    </div>
  );
};
